'use client';

import React from 'react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useLocaleRouter } from '@/hooks/useLocaleRouter';
import { trpc } from '@/lib/trpc/client';
import SignatureJourneysClient from './SignatureJourneysClient';
import { Title, Text } from '@/components/ui/Headings';

export default function SignatureJourneysPage() {
  const { t } = useTranslation();
  const { createLocalizedPath } = useLocaleRouter();
  
  
  // Fetch journeys from database
  const { data: rawJourneys = [], isLoading, error } = trpc.signatureJourneys.getAll.useQuery();

  const emptyTranslation = { en: "", de: "", uk: "" } as const;
  const emptyTranslationArray = { en: [] as string[], de: [] as string[], uk: [] as string[] } as const;
  const fallbackImage =
    "https://images.pexels.com/photos/3278215/pexels-photo-3278215.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=600";

  // Type-safe transformation of raw journey data with multilingual support and runtime guards
  const journeys = rawJourneys.map((journey) => {
    const title = (journey.title as { en?: string; de?: string; uk?: string }) || {};
    const subtitle = (journey.subtitle as { en?: string; de?: string; uk?: string }) || {};
    const description = (journey.description as { en?: string; de?: string; uk?: string }) || {};
    const country = (journey.country as { en?: string; de?: string; uk?: string }) || null;
    const highlights = (journey.highlights as { en?: string[]; de?: string[]; uk?: string[] }) || {};
    const overview = (journey.overview as { en?: string; de?: string; uk?: string }) || null;
    const whatMakesItSpecial =
      (journey.whatMakesItSpecial as { en?: string; de?: string; uk?: string }) || null;
    const itinerary =
      (journey.itinerary as {
        en?: Array<{ day: number; title: string; description: string }>;
        de?: Array<{ day: number; title: string; description: string }>;
        uk?: Array<{ day: number; title: string; description: string }>;
      }) || null;
    const includes = (journey.includes as { en?: string[]; de?: string[]; uk?: string[] }) || {};
    const notIncluded = (journey.notIncluded as { en?: string[]; de?: string[]; uk?: string[] }) || {};
    const categories = Array.isArray(journey.categories) ? (journey.categories as string[]) : [];
    const availableDates = Array.isArray(journey.availableDates)
      ? (journey.availableDates as Array<{ month: string; status: string; price: string }>)
      : null;

    return {
      ...journey,
      title: { ...emptyTranslation, ...title },
      subtitle: { ...emptyTranslation, ...subtitle },
      description: { ...emptyTranslation, ...description },
      country: country ? { ...emptyTranslation, ...country } : null,
      highlights: { ...emptyTranslationArray, ...highlights },
      overview: overview ? { ...emptyTranslation, ...overview } : null,
      whatMakesItSpecial: whatMakesItSpecial ? { ...emptyTranslation, ...whatMakesItSpecial } : null,
      itinerary: itinerary
        ? {
            en: itinerary.en ?? [],
            de: itinerary.de ?? [],
            uk: itinerary.uk ?? [],
          }
        : null,
      includes: { ...emptyTranslationArray, ...includes },
      notIncluded: { ...emptyTranslationArray, ...notIncluded },
      categories,
      availableDates,
      image:
        typeof journey.image === "string" && journey.image.trim().length > 0
          ? journey.image
          : fallbackImage,
      duration: journey.duration || "Flexible",
      groupSize: journey.groupSize || "",
      startingPrice: journey.startingPrice || "",
      currency: journey.currency || "EUR",
    };
  });

  if (isLoading) {
    return (
      <div className="pt-18 py-18 text-center">
        <Title size="md">Loading journeys...</Title>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-18 py-18 text-center">
        <Title size="md" className="mb-4">Error Loading Journeys</Title>
        <Text variant="body" className="text-red-600">{error.message}</Text>
        <Text variant="caption" className="text-gray-500 mt-2">Check console for details</Text>
      </div>
    );
  }

  return (
    <div className="pt-16">
      {/* Hero Section */}
      <section className="py-8 bg-gradient-to-br from-primary-50 to-neutral-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Title size="lg" className="mb-2">
              {t('home.signatureSection.heading')}
            </Title>
            <Text variant="subtitle" className="text-gray-700 max-w-3xl mx-auto mb-0">
              Each journey is carefully crafted to provide authentic, meaningful experiences that connect you with local communities.
            </Text>
          </div>
        </div>
      </section>

      {/* Main Content with Filters */}
      <section className="py-4 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SignatureJourneysClient journeys={journeys} />
        </div>
      </section>

      {/* Custom Journey CTA */}
      <section className="py-16 bg-gradient-to-br from-primary-50 to-neutral-cream">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Title size="md" className="mb-6">
            Don&apos;t See What You&apos;re Looking For?
          </Title>
          <Text variant="subtitle" className="text-gray-700 mb-8 max-w-2xl mx-auto">
            We specialize in creating custom journeys tailored to your specific interests, timeline, and budget. Let us craft your perfect adventure.
          </Text>
          <div className="flex justify-center">
            <Link
              href={createLocalizedPath('/contact')}
              className="inline-flex items-center px-8 py-3 text-base font-medium rounded-full text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 transition-colors shadow-md hover:shadow-lg"
            >
              Contact Our Travel Experts
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}